<template>
	<div id="app" class="d-flex flex-column wrapper">
		<navbar />
		<!-- <nutritional-banner v-if="tokenStore.hasToken()" /> -->
		<offline-banner />
		<new-version />
		<trial-banner />
		<!-- <ad-test /> -->
		<main class="flex-fill">
			<RouterView />
			<FruitRain :density="30" />
			<!--<button class="btn btn-lg btn-success" @click="testNotif">TEST</button>-->
		</main>
		<app-footer />
		<loader />
		<!-- <MobileAd v-if="Capacitor.isNativePlatform()" /> -->
		<!-- <upgrade-to-plus v-else /> -->
		<upgrade-to-plus />
		<data-policy-dialog />
		<subscription-modal />
		<onboarding-flow v-if="shouldShowOnboarding" />
	</div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { RouterView } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import Navbar from './components/Navbar.vue'
import AppFooter from './components/AppFooter.vue'
// import NutritionalBanner from "./components/NutritionalBanner.vue"
import OfflineBanner from './components/OfflineBanner.vue'
import Loader from './components/Loader.vue'
import NewVersion from './components/NewVersion.vue'
import TrialBanner from './components/TrialBanner.vue'
import DataPolicyDialog from './components/DataPolicyDialog.vue'
// import { useTokenStore } from "./stores/token"
// import { useDataPolicyStore } from "./stores/data-policy"
import { initOnlineListener, isOnline, removeOnlineListener } from './utils/networkBus'
// import { bloodSugarNotif } from "./utils/notif"
import SubscriptionModal from './components/SubscriptionModal.vue'
import UpgradeToPlus from './components/UpgradeToPlus.vue'
import FruitRain from './components/FruitRain.vue'
import { enableMealNutritionSync } from '@/plugins/Health/nutritionSync'
import OnboardingFlow from './components/OnboardingFlow.vue'
import { useUserStore } from './stores/user'
import { Preferences } from '@capacitor/preferences'
import EventBus from '@/utils/eventBus'
import {
	cancelPendingMealReminders,
	cancelTodayMealRemindersForEntry,
	loadMealReminderSettings,
	reconcileMealReminders,
	registerMealReminderTapHandler,
	requestMealReminderPermission,
	saveMealReminderSettings
} from '@/utils/mealReminders'
// import { Filesystem, Directory, Encoding } from "@capacitor/filesystem"
// import { useRouter } from "vue-router"

//UNCOMMENT WHEN TO BRING BACK ADS
// import MobileAd from "./components/MobileAd.vue"
// import { Capacitor } from "@capacitor/core"

const userStore = useUserStore()
const route = useRoute()
const router = useRouter()
// const dataPolicyStore = useDataPolicyStore()

// dataPolicyStore.loadDataPolicyAgreement()

initOnlineListener()
let removeNutritionSync = null
let mealReminderTapListener = null
let appStateListener = null
let removeMealQueuedListener = null
let removeMealAddedListener = null
let removeMealUpdatedListener = null
let removeMealDeletedListener = null
let mealReminderPromptOpen = false

const shouldShowOnboarding = computed(() => {
	const user = userStore?.user
	if (!user) return false
	return !Boolean(user.onboarding_completed)
})

const ONBOARDING_PREF_KEY = 'gv-onboarding-complete'

watch(
	() => userStore.user?.onboarding_completed,
	async (completed) => {
		if (completed) {
			try {
				await Preferences.set({ key: ONBOARDING_PREF_KEY, value: 'true' })
			} catch {
				// ignore preference errors
			}
		}
	},
	{ immediate: true }
)

async function refreshMealReminders() {
	if (!Capacitor.isNativePlatform()) return
	const userId = userStore.user?.id
	if (!userId) return
	try {
		await reconcileMealReminders(userId)
	} catch (error) {
		console.warn('Unable to reconcile meal reminders', error)
	}
}

async function maybePromptForMealReminders() {
	if (
		!Capacitor.isNativePlatform() ||
		route.name !== 'dashboard' ||
		!userStore.user?.id ||
		!userStore.user?.onboarding_completed ||
		userStore.actingAsUser ||
		mealReminderPromptOpen
	) {
		return
	}

	const settings = await loadMealReminderSettings(userStore.user.id)
	if (settings.introSeen) return

	mealReminderPromptOpen = true
	try {
		const result = await Swal.fire({
			icon: 'info',
			title: 'A gentle meal reminder?',
			text: 'Grub Vision can send one end-of-day reminder at 9:00 PM when you have not logged anything. You can customize or turn it off anytime in Profile.',
			showCancelButton: true,
			confirmButtonText: 'Allow reminders',
			cancelButtonText: 'Not now'
		})

		settings.introSeen = true
		if (!result.isConfirmed) {
			settings.dailyCatchup.enabled = false
		}
		await saveMealReminderSettings(userStore.user.id, settings)

		if (result.isConfirmed) {
			await requestMealReminderPermission()
		}
		await refreshMealReminders()
	} finally {
		mealReminderPromptOpen = false
	}
}

watch(
	[
		() => route.name,
		() => userStore.user?.id,
		() => userStore.user?.onboarding_completed,
		() => userStore.actingAsUser
	],
	() => {
		void maybePromptForMealReminders()
	},
	{ immediate: true }
)

watch(
	() => userStore.user?.id,
	async (userId, previousUserId) => {
		if (!Capacitor.isNativePlatform()) return
		if (previousUserId && previousUserId !== userId) {
			await cancelPendingMealReminders()
		}
		if (userId) await refreshMealReminders()
	},
	{ immediate: true }
)

watch(isOnline, (online) => {
	if (online) void refreshMealReminders()
})

onMounted(async () => {
	removeNutritionSync = enableMealNutritionSync()

	if (!Capacitor.isNativePlatform()) return

	mealReminderTapListener = await registerMealReminderTapHandler(router)
	appStateListener = await CapacitorApp.addListener(
		'appStateChange',
		({ isActive }) => {
			if (isActive) void refreshMealReminders()
		}
	)
	removeMealQueuedListener = EventBus.on('meal-entry-queued', (meal) => {
		if (userStore.actingAsUser) return
		void cancelTodayMealRemindersForEntry(meal)
	})
	removeMealAddedListener = EventBus.on('meal-added', () => {
		if (userStore.actingAsUser) return
		void refreshMealReminders()
	})
	removeMealUpdatedListener = EventBus.on('meal-updated', () => {
		if (userStore.actingAsUser) return
		void refreshMealReminders()
	})
	removeMealDeletedListener = EventBus.on('meal-deleted', () => {
		if (userStore.actingAsUser) return
		void refreshMealReminders()
	})
})

// async function testNotif() {
// 	await bloodSugarNotif("lunch", 0.01, new Date().toISOString())
// }

// onBeforeMount(() => {
// 	//inject a script tag to the head of the document if it does not already exist
// 	if (!document.getElementById("ad-test") && !Capacitor.isNativePlatform()) {
// 		const script = document.createElement("script")
// 		script.id = "ad-test"
// 		script.src =
// 			"https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6225091491024428"
// 		script.crossOrigin = "anonymous"
// 		script.async = true
// 		document.head.appendChild(script)
// 	}
// })

// onBeforeMount(async () => {
// 	// await tokenStore.loadTokens()
// 	App.addListener("appRestoredResult", async (event) => {
// 		if (event.pluginId === "Camera" && event.success) {
// 			//restore
// 			//read the state.json file
// 			const state = await Filesystem.readFile({
// 				path: "state.json",
// 				directory: Directory.Data,
// 				encoding: Encoding.UTF8,
// 			})
// 			// alert(state.data)
// 			const stateObj = JSON.parse(state.data)
// 			// alert("GOING TO: " + stateObj.route)

// 			// // //inject the camera data into the state
// 			// stateObj.cameraData = event.data.dataUrl

// 			// //save to file
// 			// await Filesystem.writeFile({
// 			// 	path: "state.json",
// 			// 	data: JSON.stringify(stateObj),
// 			// 	directory: Directory.Data,
// 			// })

// 			//now redirect to the route
// 			// nextTick(() => {
// 			router.push({
// 				path: stateObj.route,
// 				query: { restored: "from_camera" },
// 			})
// 			// })
// 		}
// 	})
// })

onBeforeUnmount(async () => {
	removeNutritionSync?.()
	await Promise.all([
		mealReminderTapListener?.remove(),
		appStateListener?.remove()
	])
	removeMealQueuedListener?.()
	removeMealAddedListener?.()
	removeMealUpdatedListener?.()
	removeMealDeletedListener?.()
	removeOnlineListener()
	// App.removeAllListeners()
})
</script>

<style scoped>
.wrapper {
	min-height: 100vh;
	flex-direction: column;
	display: flex;
	position: relative;
	z-index: 1; /* ensure all app content is above background effects like FruitRain */
}

main.flex-fill {
	flex: 1 0 auto;
	position: relative;
	z-index: 2;
}

:deep(footer) {
	position: relative;
	z-index: 1;
}
</style>
